/* eslint-disable react-hooks/exhaustive-deps */
import { useLayoutEffect, useState } from "react";
import dayjs from "dayjs";
import { displayMessage, postAPI, getAPI } from "../../utils/common";
import {
  DatePicker,
  Table,
  Card,
  Space,
  Button,
  Input,
  Popconfirm,
  Layout,
  Modal,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import {
  GET_ALL_AIOSELL_LOGS,
  DELETE_LOGS_BY_DATE,
  TRUNCATE_LOGS,
} from "../../constants/api";
import { SUCCESS_MSG_TYPE } from "../../constants/messages";

const { Content } = Layout;
const { RangePicker } = DatePicker;

function LogsTable() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);

  //
  const [modalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const [modalTitle, setModalTitle] = useState("");

  const showModal = (title, content) => {
    setModalTitle(title);
    setModalContent(content);
    setModalVisible(true);
  };

  useLayoutEffect(() => {
    const today = dayjs();
    const weekAgo = today.subtract(2, "day");
    setSelectedDates([weekAgo, today]);
    fetchLogs(weekAgo, today);
  }, []);

  const fetchLogs = (start, end) => {
    setLoading(true);
    getAPI(
      `${GET_ALL_AIOSELL_LOGS}?start_date=${start.format(
        "YYYY-MM-DD"
      )}&end_date=${end.format("YYYY-MM-DD")}`,
      (res) => {
        setLoading(false);
        if (res.status) {
          setLogs(res.data);
          setFilteredLogs(res.data);
        }
      },
      () => setLoading(false)
    );
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = logs.filter((log) =>
      log.endpoint.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredLogs(filtered);
  };

  const handleDateFilter = (dates) => {
    setSelectedDates(dates);
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      fetchLogs(start, end);
    } else {
      fetchLogs(dayjs().subtract(7, "day"), dayjs()); // fallback to 1 week
    }
  };

  const handleDeleteByDate = () => {
    if (selectedDates.length !== 2) return;
    const [start, end] = selectedDates;
    postAPI(
      DELETE_LOGS_BY_DATE,
      {
        start_date: start.format("YYYY-MM-DD"),
        end_date: end.format("YYYY-MM-DD"),
      },
      (res) => {
        displayMessage(SUCCESS_MSG_TYPE, "Logs deleted successfully");
        const [start, end] = selectedDates;
        fetchLogs(start, end);
      }
    );
  };

  const handleTruncate = () => {
    postAPI(TRUNCATE_LOGS, {}, (res) => {
      displayMessage(SUCCESS_MSG_TYPE, "All logs truncated");
      const [start, end] = selectedDates;
      fetchLogs(start, end);
    });
  };

  const refresh = () => {
    const [start, end] = selectedDates;
    fetchLogs(start, end);
  };

  const columns = [
    {
      title: "No.",
      dataIndex: "index",
      width: 60,
      render: (_text, _record, index) => index + 1,
    },
    {
      title: "ID",
      dataIndex: "id",
      width: 60,
    },
    {
      title: "Endpoint",
      dataIndex: "endpoint",
      width: 250,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      render: (text) => text?.toUpperCase(),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      width: 180,
      render: (text) => dayjs(text).format("DD MMM YYYY, hh:mm:ss A"),
    },
    {
      title: "Payload",
      dataIndex: "payload",
      width: 150,
      ellipsis: true,
      render: (payload) => (
        <Button
          size="small"
          onClick={() => showModal("Payload", JSON.stringify(payload, null, 2))}
        >
          View
        </Button>
      ),
    },
    {
      title: "Response",
      dataIndex: "response",
      width: 150,
      ellipsis: true,
      render: (response) => (
        <Button
          size="small"
          onClick={() =>
            showModal("Response", JSON.stringify(response, null, 2))
          }
        >
          View
        </Button>
      ),
    },
  ];

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
  };

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Aiosell Logs"
          extra={
            <Space>
              <Input
                placeholder="Search by Endpoint"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <RangePicker value={selectedDates} onChange={handleDateFilter} />
              <Button
                loading={loading}
                disabled={loading}
                type="primary"
                onClick={refresh}
              >
                Refresh
              </Button>
              <Popconfirm
                title="Delete logs for selected date range?"
                onConfirm={handleDeleteByDate}
              >
                <Button
                  loading={loading}
                  disabled={loading}
                  danger
                  icon={<DeleteOutlined />}
                >
                  Delete by Date
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Are you sure to truncate all logs?"
                onConfirm={handleTruncate}
              >
                <Button loading={loading} disabled={loading} danger>
                  Truncate All
                </Button>
              </Popconfirm>
            </Space>
          }
        >
          <Table
            loading={loading}
            columns={columns}
            dataSource={filteredLogs}
            rowKey="id"
            pagination={tableParams.pagination}
            scroll={{ x: 1200 }}
            onChange={handleTableChange}
          />
        </Card>
      </Content>
      <Modal
        title={modalTitle}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {modalContent}
        </pre>
      </Modal>

      <LayoutFooter />
    </Layout>
  );
}

export default LogsTable;
